import * as React from 'react';
import withStyles from 'react-jss';
import Button from 'antd/lib/button';
import Icon from 'antd/lib/icon';
import Modal from 'antd/lib/modal';
import { buttonStyle } from 'src/Library/CommonStyles';
import { StylesProps } from 'src/Library/Types';
import { BookingStats } from '../InquiryUtils';
import { tokens } from 'src/design-system';

const styles = () => ({
  modal: {
    '& .ant-modal-header': {
      borderBottom: '1px solid rgba(225, 225, 225, 0.2)',
    },
    '& .ant-modal-body': {
      padding: '16px 24px 24px 24px',
      color: '#666666',
      lineHeight: '22px',
    },
    '& .ant-modal-footer': {
      borderTop: '1px solid rgba(225, 225, 225, 0.2)',
    },
  },
  warningHeader: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: '16px',
  },
  warningIcon: {
    color: '#FAAD14',
    fontSize: '22px',
    marginRight: '12px',
  },
  warningMessage: {
    color: '#424242',
    fontSize: `${tokens.fontSize.body}px`,
    fontWeight: tokens.fontWeight.semibold,
  },
  statsContainer: {
    backgroundColor: '#FAFAFA',
    borderRadius: `${tokens.radius.md}px`,
    marginBottom: '16px',
    padding: '12px 16px',
  },
  statsTitle: {
    color: '#424242',
    fontSize: `${tokens.fontSize.small}px`,
    fontWeight: tokens.fontWeight.semibold,
    marginBottom: '8px',
  },
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '16px',
  },
  statItem: {
    alignItems: 'center',
    display: 'flex',
    minWidth: '100px',
  },
  statLabel: {
    color: '#666666',
    fontSize: `${tokens.fontSize.small}px`,
    marginRight: '4px',
  },
  statValue: {
    color: '#424242',
    fontSize: `${tokens.fontSize.small}px`,
    fontWeight: tokens.fontWeight.semibold,
  },
  guidanceText: {
    color: '#666666',
    fontSize: `${tokens.fontSize.small}px`,
    lineHeight: '18px',
  },
  cancelButton: {
    color: '#666666',
    cursor: 'pointer',
  },
  proceedButton: {
    ...buttonStyle(tokens.colors.bgWhite, tokens.colors.primaryDark, '1px solid #006EC3'),
    borderRadius: `${tokens.radius.sm}px`,
    marginLeft: '14px',
    padding: '0 22px',
  },
});

interface ExistingBookingAlertModalProps extends StylesProps<ReturnType<typeof styles>> {
  bidBookingStats?: BookingStats;
  inquiryBookingStats?: BookingStats;
  isVisible: boolean;
  onCancel: () => void;
  onProceed: () => void;
}

const ExistingBookingAlertModal = (props: ExistingBookingAlertModalProps): React.ReactElement => {
  const {
    bidBookingStats,
    classes,
    inquiryBookingStats,
    isVisible,
    onCancel,
    onProceed,
  } = props;

  const renderStatItem = (label: string, value: number): React.ReactElement => (
    <div className={classes.statItem}>
      <span className={classes.statLabel}>{label}:</span>
      <span className={classes.statValue}>{value}</span>
    </div>
  );

  const renderBookingStats = (stats: BookingStats, title: string): React.ReactElement => (
    <div className={classes.statsContainer}>
      <div className={classes.statsTitle}>{title}</div>
      <div className={classes.statsRow}>
        {renderStatItem('Booked', stats.confirmed)}
        {renderStatItem('Requested', stats.open)}
        {renderStatItem('Declined', stats.declined)}
        {renderStatItem('Cancelled', stats.cancelled)}
      </div>
    </div>
  );

  const getTotalBookingCount = (stats?: BookingStats): number => {
    if (!stats) return 0;
    return stats.confirmed + stats.open + stats.declined + stats.cancelled;
  };

  const getWarningMessage = (): string => {
    const inquiryTotal = getTotalBookingCount(inquiryBookingStats);
    const bidTotal = getTotalBookingCount(bidBookingStats);
    const totalBookings = inquiryTotal + bidTotal;
    const isPlural = totalBookings > 1;
    const object = inquiryBookingStats ? 'inquiry' : 'bid';
    return isPlural
      ? `Bookings already exist for this ${object}`
      : `A booking already exists for this ${object}`;
  };

  return (
    <Modal
      className={classes.modal}
      footer={
        <React.Fragment>
          <span className={classes.cancelButton} onClick={onCancel}>
            Cancel
          </span>
          <Button className={classes.proceedButton} onClick={onProceed}>
            Proceed
          </Button>
        </React.Fragment>
      }
      onCancel={onCancel}
      title="Existing Booking Alert"
      visible={isVisible}
      width={560}
      zIndex={1000}
    >
      <div className={classes.warningHeader}>
        <Icon className={classes.warningIcon} theme="filled" type="exclamation-circle" />
        <span className={classes.warningMessage}>
          {getWarningMessage()}
        </span>
      </div>

      {inquiryBookingStats && renderBookingStats(inquiryBookingStats, 'Inquiry Level')}
      {bidBookingStats && renderBookingStats(bidBookingStats, 'Bid Level')}

      <div className={classes.guidanceText}>
        Are you sure you want to proceed with creating a new booking?
      </div>
    </Modal>
  );
};

export default withStyles(styles)(React.memo(ExistingBookingAlertModal));
